// MAS 精靈：派系（School）+ 招式（Move）對應表
// =====================================================
// 命名邏輯：
//   Step 1 ambition → 派系（school）：磐石 / 天秤 / 雷霆
//   Revelation 同派系內三招式 → 守 / 衡 / 銳
//
// 6 軸雷達圖：報酬力 / 防禦力 / 穩定度 / 爆發力 / 命中率 / 適應性 (0~100)
// =====================================================

export const RADAR_AXES = [
  { key: "return", label: "報酬力" },
  { key: "defense", label: "防禦力" },
  { key: "stability", label: "穩定度" },
  { key: "burst", label: "爆發力" },
  { key: "winRate", label: "命中率" },
  { key: "adaptability", label: "適應性" },
];

// ---- ambition ↔ riskTier 映射（給 API query 用）----
export const AMBITION_TO_RISK_TIER = {
  steady: "LOW",
  balanced: "MID",
  hunter: "HIGH",
};

export const RISK_TIER_TO_AMBITION = {
  LOW: "steady",
  MID: "balanced",
  HIGH: "hunter",
};

// ---- 招式的雷達 fallback 值（爆發力 / 適應性：當 elite 無 monthlyReturns 月報資料時退此預設）----
//   part C 起：有月報資料就改用真實數據算（見 eliteToRadar）；舊 elite（未重跑回測）仍退這裡
const MOVE_BURST_DEFAULT = { shou: 22, heng: 55, rui: 88 };
const MOVE_ADAPT_DEFAULT = { shou: 68, heng: 82, rui: 60 };

const clamp = (v, min = 0, max = 100) => Math.max(min, Math.min(max, v));

// 取得「真實年化報酬」%：優先用 API 的 annualReturn；缺失才用 monthlyReturn 複利推算
function getRealAnnualReturnPct(elite) {
  const ar = Number(elite?.annualReturn);
  if (!Number.isNaN(ar) && ar > 0) return ar;
  const mr = Number(elite?.monthlyReturn || 0);
  return (Math.pow(1 + mr / 100, 12) - 1) * 100;
}

// 把 elite 物件（API 返回的策略）轉成 6 軸雷達數值
//   4 軸用 KPI 公式：
//     - 報酬力：用 annualReturn 的 log10 scale → 高報酬策略也能差異化、不撞 100 天花板
//     - 防禦力：100 − MDD × 2
//     - 穩定度：(PF − 1) × 50
//     - 命中率：winRate（後端 dataQuality.hasWinRate=false 時回 null，不假裝 0/50 拉歪面積）
//   2 軸（爆發力、適應性）part C 起改用 backend 月報酬序列 monthlyReturns 計算，資料不足才退招式預設：
//     - 爆發力：最佳單月報酬 %（log 縮放，不易撞頂）；月份 < 2 → 退預設
//     - 適應性：正報酬月份占比 ×100（時間一致性）；月份 < 3（不夠評估一致性）→ 退預設
//   ⚠️ monthlyReturns 需「重跑回測 + 部署新後端」才有；現有 elite 為 null → 兩軸照舊退預設（零回歸）
//
// ⚠️ 命中率軸可能是 null（後端旗標說沒這筆資料）：
//    呼叫端要處理 null（RadarChart6 跳過該點、DotBar 顯示「—」）
export function eliteToRadar(elite, moveKey = "heng") {
  if (!elite) {
    return { return: 0, defense: 0, stability: 0, burst: 0, winRate: null, adaptability: 0 };
  }
  const annualPct = Math.max(getRealAnnualReturnPct(elite), 0);
  // log10 縮放：ar=10% → 37、50% → 60、100% → 70、500% → 94、1000%+ → 100
  // 比原本 monthlyReturn × 6 更能在高月報區段保留差異
  const returnAxis = Math.log10(annualPct + 1) * 35;
  // 命中率軸：後端 dataQuality.hasWinRate=false 或 winRate=null → 回 null
  const hasWinRate = elite.dataQuality?.hasWinRate !== false && elite.winRate != null;

  // ⭐ part C：爆發力 / 適應性 改用月報酬序列（缺資料退招式預設）
  const months = Array.isArray(elite.monthlyReturns)
    ? elite.monthlyReturns.filter((m) => m && m.returnPct != null)
    : [];
  const burst =
    months.length >= 2
      ? Math.round(clamp(Math.log10(Math.max(...months.map((m) => Number(m.returnPct) || 0), 0) + 1) * 55))
      : (MOVE_BURST_DEFAULT[moveKey] || 50);
  const adaptability =
    months.length >= 3
      ? Math.round(clamp((months.filter((m) => Number(m.returnPct) > 0).length / months.length) * 100))
      : (MOVE_ADAPT_DEFAULT[moveKey] || 50);

  return {
    return: Math.round(clamp(returnAxis)),
    defense: Math.round(clamp(100 - Number(elite.maxDrawdown || 0) * 2)),
    stability: Math.round(clamp((Number(elite.profitFactor || 1) - 1) * 50)),
    burst,
    winRate: hasWinRate ? Math.round(clamp(Number(elite.winRate))) : null,
    adaptability,
  };
}

// ---- 派系（School）：對應 Step 1 ambition ----
export const SCHOOLS = {
  steady: {
    key: "steady",
    name: "磐石",
    desc: "以最低風險換取穩定回報",
    icon: "Shield",
    accent: "emerald",
    accentHex: "#10b981",
    bgSoft: "rgba(16, 185, 129, 0.10)",
  },
  balanced: {
    key: "balanced",
    name: "天秤",
    desc: "在風險與報酬間取得最佳平衡",
    icon: "Scale",
    accent: "indigo",
    accentHex: "#6366f1",
    bgSoft: "rgba(99, 102, 241, 0.10)",
  },
  hunter: {
    key: "hunter",
    name: "雷霆",
    desc: "以可控風險換取高額成長",
    icon: "Zap",
    accent: "amber",
    accentHex: "#f59e0b",
    bgSoft: "rgba(245, 158, 11, 0.10)",
  },
};

// ---- 招式（Move）：派系內三個子層級 ----
// lucideIcon + accentHex 給 Revelation 卡片用，每張卡視覺完全不同
//   主色（卡片大 icon、雷達、按鈕）= 招式色（綠/紫/紅）→ 三卡一眼區分
//   派系色保留在卡片右上「派系徽章」，提示用戶這 3 張都屬同一派系
export const MOVES = [
  {
    key: "shou", label: "守", tag: "沉穩", lucideIcon: "ShieldCheck",
    accentHex: "#10b981", bgSoft: "rgba(16, 185, 129, 0.10)",
    tagline: "派系內最沉穩，重視防禦",
  },
  {
    key: "heng", label: "衡", tag: "平衡", lucideIcon: "Target",
    accentHex: "#8b5cf6", bgSoft: "rgba(139, 92, 246, 0.10)",
    tagline: "派系內最平衡，攻守兼顧",
  },
  {
    key: "rui",  label: "銳", tag: "進取", lucideIcon: "Flame",
    accentHex: "#f43f5e", bgSoft: "rgba(244, 63, 94, 0.10)",
    tagline: "派系內最進取，向上限拓進",
  },
];

// ---- 9 種組合的範本（KPI + 雷達 + 文案）----
// PR 3 後端串接後可被 API 真實資料覆蓋。
const TEMPLATES = {
  steady: {
    shou: {
      tagline: "極致防守，把守每一分本金",
      kpi: { monthlyReturn: 1.5, maxDrawdown: 4,  profitFactor: 1.65, winRate: 95, tradeCount: 28, symbol: "EURUSD", timeframe: "D1" },
      radar: { return: 20, defense: 98, stability: 92, burst: 10, winRate: 95, adaptability: 55 },
    },
    heng: {
      tagline: "穩健為主，伺機而動",
      kpi: { monthlyReturn: 3.2, maxDrawdown: 8,  profitFactor: 1.55, winRate: 90, tradeCount: 45, symbol: "EURUSD", timeframe: "H4" },
      radar: { return: 35, defense: 92, stability: 88, burst: 22, winRate: 90, adaptability: 65 },
    },
    rui: {
      tagline: "穩健中尋找早期機會",
      kpi: { monthlyReturn: 5.0, maxDrawdown: 13, profitFactor: 1.48, winRate: 82, tradeCount: 65, symbol: "USDJPY", timeframe: "H1" },
      radar: { return: 50, defense: 85, stability: 80, burst: 38, winRate: 82, adaptability: 72 },
    },
  },
  balanced: {
    shou: {
      tagline: "先求穩固，再追成長",
      kpi: { monthlyReturn: 4.5, maxDrawdown: 12, profitFactor: 1.70, winRate: 78, tradeCount: 50, symbol: "GBPUSD", timeframe: "H4" },
      radar: { return: 50, defense: 82, stability: 85, burst: 38, winRate: 78, adaptability: 80 },
    },
    heng: {
      tagline: "風險與報酬最對稱的中段策略",
      kpi: { monthlyReturn: 5.2, maxDrawdown: 18.5, profitFactor: 1.82, winRate: 70, tradeCount: 60, symbol: "GBPUSD", timeframe: "M30" },
      radar: { return: 65, defense: 70, stability: 78, burst: 55, winRate: 70, adaptability: 88 },
    },
    rui: {
      tagline: "順勢操作，把握中段成長",
      kpi: { monthlyReturn: 8.5, maxDrawdown: 24, profitFactor: 1.75, winRate: 62, tradeCount: 85, symbol: "EURJPY", timeframe: "M30" },
      radar: { return: 80, defense: 55, stability: 68, burst: 72, winRate: 62, adaptability: 85 },
    },
  },
  hunter: {
    shou: {
      tagline: "精準出手，鎖定高勝率時機",
      kpi: { monthlyReturn: 9.0, maxDrawdown: 28, profitFactor: 2.05, winRate: 58, tradeCount: 80, symbol: "XAUUSD", timeframe: "M30" },
      radar: { return: 82, defense: 45, stability: 58, burst: 78, winRate: 58, adaptability: 65 },
    },
    heng: {
      tagline: "高頻交易，快速累積成果",
      kpi: { monthlyReturn: 12.5, maxDrawdown: 35, profitFactor: 2.18, winRate: 50, tradeCount: 120, symbol: "XAUUSD", timeframe: "M15" },
      radar: { return: 92, defense: 30, stability: 45, burst: 90, winRate: 50, adaptability: 55 },
    },
    rui: {
      tagline: "全面進攻，挑戰最高報酬",
      kpi: { monthlyReturn: 18.0, maxDrawdown: 45, profitFactor: 2.30, winRate: 42, tradeCount: 165, symbol: "BTCUSD", timeframe: "M5" },
      radar: { return: 98, defense: 15, stability: 30, burst: 98, winRate: 42, adaptability: 42 },
    },
  },
};

// ---- 5 種策略類型（新命名，2026-05-22 取代「派系+招式」）----
// 改動原因：原本「磐石守/天秤衡/雷霆銳」等武俠風命名對用戶意義不明確，
// 改成直觀策略類型 + 一句話 tagline，用戶一眼看出策略屬性。
// 注意：此為 placeholder，school+move → strategy type 的映射只是「隨便塞」，
// 之後需依每個 elite 的實際特性 (TACTICAL_GENE / aiSummary) 重新對應。
export const STRATEGY_TYPES = [
  {
    key: "trend_breakout",
    name: "趨勢突破策略",
    type: "趨勢突破型",
    tagline: "精準捕捉行情啟動，順勢乘風而上",
  },
  {
    key: "mean_reversion",
    name: "均線回歸策略",
    type: "均值回歸型",
    tagline: "逢低吸納、逢高出脫，賺取均值修復",
  },
  {
    key: "swing_follow",
    name: "波段順勢策略",
    type: "波段順勢型",
    tagline: "咬住主升段，階梯式擴大利潤",
  },
  {
    key: "low_freq_defense",
    name: "低頻防守策略",
    type: "低頻防守型",
    tagline: "少出手，但每一手都帶著紀律",
  },
  {
    key: "range_oscillation",
    name: "區間震盪策略",
    type: "區間震盪型",
    tagline: "高頻在區間裡來回收割",
  },
];

// 9 個 school×move 組合 → 5 種策略類型的 placeholder 對應表
// ⚠️ 此 mapping 是「隨便塞」，沒有任何理論依據，純為 UI 過渡顯示。
// 之後預計改成：依 elite.aiSummary / TACTICAL_GENE 動態判斷每個 elite 屬於哪型。
const SCHOOL_MOVE_TO_STRATEGY = {
  "steady-shou":     "low_freq_defense",   // 磐石守 → 低頻防守
  "steady-heng":     "range_oscillation",  // 磐石衡 → 區間震盪
  "steady-rui":      "mean_reversion",     // 磐石銳 → 均線回歸
  "balanced-shou":   "mean_reversion",     // 天秤守 → 均線回歸
  "balanced-heng":   "swing_follow",       // 天秤衡 → 波段順勢
  "balanced-rui":    "trend_breakout",     // 天秤銳 → 趨勢突破
  "hunter-shou":     "swing_follow",       // 雷霆守 → 波段順勢
  "hunter-heng":     "trend_breakout",     // 雷霆衡 → 趨勢突破
  "hunter-rui":      "trend_breakout",     // 雷霆銳 → 趨勢突破
};

// ---- 公用 helpers ----
export function getSchool(ambitionKey) {
  return SCHOOLS[ambitionKey] || SCHOOLS.balanced;
}

export function getMove(moveKey) {
  return MOVES.find((m) => m.key === moveKey) || MOVES[1];
}

export function getTemplate(ambitionKey, moveKey) {
  return TEMPLATES[ambitionKey]?.[moveKey] || TEMPLATES.balanced.heng;
}

// 依 school + move 找出對應的策略類型 (placeholder mapping)
export function getStrategyType(schoolKey, moveKey) {
  const k = `${schoolKey}-${moveKey}`;
  const typeKey = SCHOOL_MOVE_TO_STRATEGY[k] || "swing_follow";
  return STRATEGY_TYPES.find(t => t.key === typeKey) || STRATEGY_TYPES[2];
}

// 註：以下舊 helper 已隨真實 API 串接（PR 3）退役，移除避免 mock 資料污染 UI：
//   getRevelationCards()  — 已由 fetchElites + pickRevelationCards 取代
//   resolveSavedStrategy() — 已由 fetchMyStrategies 直接拿 elite 資料取代
//   strategyToRadar()     — 已由 eliteToRadar 取代（用 annualReturn 而非月報複利）
//   getArchetype()        — 由 ambition→school 直接映射取代
